import OSS from "ali-oss";
import { http } from "@/utils/request";

// 固定凭证接口
interface StaticCredentials {
  accessKeyId: string;
  accessKeySecret: string;
  region: string;
  bucket: string;
}

// 临时凭证接口
interface Credentials {
  tmpSecretId: string;
  tmpSecretKey: string;
  sessionToken: string;
  region: string;
  bucket: string;
  expiredTime: number;
}

class OSSManager {
  private static instance: OSSManager;
  private client: OSS | null = null;
  private credentials: Credentials | null = null;
  private useStaticCredentials: boolean = false;
  private staticCredentials: StaticCredentials | null = null;

  static getInstance() {
    if (!OSSManager.instance) {
      OSSManager.instance = new OSSManager();
    }
    return OSSManager.instance;
  }

  // 设置固定凭证
  setStaticCredentials(credentials: StaticCredentials) {
    this.staticCredentials = credentials;
    this.useStaticCredentials = true;
    this.resetClient(); // 重置客户端，使新凭证生效
    return this;
  }

  // 切换到使用临时凭证
  useTemporaryCredentials() {
    this.useStaticCredentials = false;
    this.resetClient(); // 重置客户端，使新设置生效
    return this;
  }

  // 检查凭证是否过期
  private isExpired(): boolean {
    // 如果使用固定凭证，永不过期
    if (this.useStaticCredentials) return false;

    if (!this.credentials) return true;

    // 提前5分钟更新凭证
    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime + 300 >= this.credentials.expiredTime;
  }

  private async getCredentials(): Promise<Credentials> {
    try {
      // 使用 http 工具发送请求
      const credentials = await http.get<Credentials>("oss/credential");
      return credentials;
    } catch (error) {
      console.error("Failed to get OSS credentials:", error);
      throw new Error("Failed to get OSS credentials");
    }
  }

  private async getClient(): Promise<OSS> {
    if (!this.client || this.isExpired()) {
      if (this.useStaticCredentials && this.staticCredentials) {
        // 使用固定凭证
        this.client = new OSS({
          region: this.staticCredentials.region,
          accessKeyId: this.staticCredentials.accessKeyId,
          accessKeySecret: this.staticCredentials.accessKeySecret,
          bucket: this.staticCredentials.bucket,
          secure: true,
        });
      } else {
        // 使用临时凭证
        this.credentials = await this.getCredentials();
        this.client = new OSS({
          region: this.credentials.region,
          accessKeyId: this.credentials.tmpSecretId,
          accessKeySecret: this.credentials.tmpSecretKey,
          stsToken: this.credentials.sessionToken,
          bucket: this.credentials.bucket,
          secure: true,
        });
      }
    }
    return this.client;
  }

  async doUpload<T>(operation: (client: OSS) => Promise<T>): Promise<T> {
    try {
      const client = await this.getClient();
      return await operation(client);
    } catch (error: any) {
      if (
        !this.useStaticCredentials &&
        (error.code === "InvalidAccessKeyId" ||
          error.code === "SecurityTokenExpired")
      ) {
        // 临时凭证过期，重置客户端
        this.client = null;
        this.credentials = null;
        // 重试一次
        return this.doUpload(operation);
      }
      throw error;
    }
  }

  // 重置客户端
  resetClient() {
    this.client = null;
    this.credentials = null;
  }
}

// 文件上传函数
export const uploadFile = async (
  file: File,
  onProgress?: (percent: number) => void,
  customPath?: string
): Promise<string> => {
  try {
    const ossManager = OSSManager.getInstance();

    return await ossManager.doUpload(async (client) => {
      // 可以自定义上传路径，否则使用默认路径
      const key =
        customPath ||
        `course/${Date.now()}-${Math.random().toString(36).slice(-6)}-${
          file.name
        }`;

      const result = await client.multipartUpload(key, file, {
        progress: (p) => onProgress?.(Math.floor(p * 100)),
        parallel: 4,
        partSize: 1024 * 1024,
      });

      console.log(result);
      console.log(client);
      console.log(result.name);
      console.log(key);
      return result.name;
    });
  } catch (error) {
    console.error("Upload failed:", error);
    throw error;
  }
};

// 导出实例，方便其他地方使用
export const ossManager = OSSManager.getInstance();

// 设置固定凭证的辅助函数
export const configureStaticOSS = (credentials: StaticCredentials) => {
  return ossManager.setStaticCredentials(credentials);
};
